import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfigStateService, LocalizationPipe } from '@abp/ng.core';
import { Observable, switchMap } from 'rxjs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { FORM_SHARED_IMPORTS } from 'src/app/shared/imports/form-shared.imports';
import { CallRecordingSettingsDto } from 'src/core/models/settings/call-recording-settings/call-recording-settings.dto';
import { CustomSettingsService } from 'src/app/proxy/custom-settings.service';
import type { SettingsDto } from 'src/app/proxy/dtos/settings/models';

/** Setting names as registered by the backend's `CallRecordingSettingsProvider`. */
const STREAMING_ENABLED_SETTING = 'CallRecording.StreamingEnabled';
const STREAMING_WSS_URL_SETTING = 'CallRecording.StreamingWssUrl';

/**
 * Absolute `ws://`/`wss://` URL. Kept in step with the backend's `TryParseRecorderEndpoint`, which drops a
 * tenant's audio-stream extension when the stored value is not an absolute WebSocket URL — the failure is
 * only visible in the API log, so it is worth catching in the form.
 *
 * <p>Surrounding whitespace is tolerated because a pasted URL often carries it and the submit trims;
 * rejecting it would surface as an invalid-URL error the user cannot see the cause of.</p>
 */
const WEBSOCKET_URL_PATTERN = /^\s*wss?:\/\/[^\s/$.?#].[^\s]*\s*$/i;

/**
 * Integration &gt; Call Recording tab: opts the current tenant into streaming its call audio to the
 * external call-recorder service and holds that service's WebSocket endpoint.
 *
 * <p>Tenant-scoped by construction — the whole Integration screen is removed for the ABP host user
 * (see `AppComponent`), and the backend restricts both settings to the tenant value provider.</p>
 */
@Component({
  selector: 'app-call-recording-settings',
  standalone: true,
  templateUrl: './call-recording-settings.component.html',
  styleUrls: ['../integration-settings-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...FORM_SHARED_IMPORTS, LocalizationPipe, ToggleSwitchModule],
})
export class CallRecordingSettingsComponent extends BaseFormComponent<CallRecordingSettingsDto> {
  private readonly customSettingsService = inject(CustomSettingsService);
  private readonly configStateService = inject(ConfigStateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super(inject(ChangeDetectorRef));
  }

  protected buildForm(): FormGroup {
    return new FormGroup({
      streamingEnabled: new FormControl<boolean>(false, { nonNullable: true }),
      streamingWssUrl: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.pattern(WEBSOCKET_URL_PATTERN)],
      }),
    });
  }

  protected override afterFormInit(): void {
    this.loadSettings();
    this.applyUrlRequirement();

    this.form.controls['streamingEnabled'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyUrlRequirement();
        this.cdr.markForCheck();
      });
  }

  protected submitRequest(value: CallRecordingSettingsDto): Observable<unknown> {
    const settings: SettingsDto[] = [
      {
        settingName: STREAMING_ENABLED_SETTING,
        settingValue: String(value.streamingEnabled),
      },
      {
        settingName: STREAMING_WSS_URL_SETTING,
        settingValue: value.streamingWssUrl.trim(),
      },
    ];

    // Both values always go up, empty URL included: clearing the endpoint is a legitimate edit, and the
    // tenant's stored row is the only thing the dialplan generator reads.
    return this.customSettingsService
      .set(settings)
      .pipe(switchMap(() => this.configStateService.refreshAppState()));
  }

  /** Localized hint shown when the endpoint is not an absolute ws/wss URL. */
  getWebSocketUrlInvalidMessage(): string {
    return this.localizationService.instant('Settings::CallRecordingStreamingWssUrlInvalid');
  }

  private loadSettings(): void {
    const enabled = this.configStateService.getSetting(STREAMING_ENABLED_SETTING);

    this.setValue({
      streamingEnabled: enabled?.toLowerCase() === 'true',
      streamingWssUrl: this.configStateService.getSetting(STREAMING_WSS_URL_SETTING) || '',
    });
  }

  /**
   * The endpoint is only mandatory while streaming is on; an opted-out tenant may leave it blank without
   * the form blocking the save.
   */
  private applyUrlRequirement(): void {
    const enabled = this.form.controls['streamingEnabled'].value as boolean;
    const url = this.form.controls['streamingWssUrl'];

    url.setValidators(
      enabled
        ? [Validators.required, Validators.pattern(WEBSOCKET_URL_PATTERN)]
        : [Validators.pattern(WEBSOCKET_URL_PATTERN)]
    );
    url.updateValueAndValidity({ emitEvent: false });
  }
}
